import {Component, DestroyRef, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GrantChangeNotifierService} from '../../_modules/company/access/grant-change-notifier.service';
import {AuthenticationService} from '../authentication.service';
import {MyGrantsPanelComponent} from './my-grants-panel.component';
import {SelfActivationPanelComponent} from './self-activation-panel.component';
import {TileImpersonationPanelComponent} from './tile-impersonation-panel.component';
import {readErrorCode} from '../two-factor/second-factor-errors';
import {
  TWO_FACTOR_REMEDIATION_URL,
  contextEntryMessageFor,
  isSelfRemediableContextRefusal
} from './context-entry-refusal';
import {
  ContextOptions,
  ContextRecoveryNotice,
  DormantContext,
  SelectableContext,
  SelectableContextKind,
  contextKey,
  contextScope,
  contextTitle,
  dormantContextMessage,
  needsEntryAfterSignIn,
  roleLabel,
  viewLabel
} from './context.models';

/**
 * The chooser for a session that has landed with **no context and more than one option**.
 *
 * ### Why its own route rather than a panel inside the app
 *
 * Three reasons, in order of how much they cost to get wrong:
 *
 * 1. **The shell that would host a panel is itself a guess.** `app.component.html` picks between the admin
 *    navbar, the employee panel and the operator shell from the session's roles. A session that has not chosen
 *    a context has no settled roles, so any panel rendered inside it would be framed by chrome picked for a
 *    context the user has not selected — and on this exact session (`PlatformOwner` + a company grant) the
 *    guard would first bounce it to the platform landing.
 * 2. **The landing is a full document load.** Sign-in, redeem and every switch finish with
 *    `window.open('/', '_self')`, so component state does not survive the boundary. A route does; a transient
 *    "I was about to ask you something" flag does not.
 * 3. **It is a step, not an aside.** Unlike the sign-in notice (which must never stand between the person and
 *    the app), there is genuinely nothing to show behind this: without a context the session cannot load a
 *    single tenant-scoped screen. It renders as a plain page WITHIN the app chrome, though — the chrome is kept
 *    on this route deliberately (see `chrome-free-routes.ts`: the identity the navbar names here is the
 *    visitor's own), so the page draws no header or background of its own.
 *
 * It is reached from {@link SigningInComponent} right after the handoff exchange — the one moment the choice is
 * actually being made — and never as a recurring interruption. The chrome's
 * {@link ContextSwitcherComponent} covers every later change of mind.
 */
@Component({
  selector: 'auth-choose-context',
  standalone: true,
  imports: [MyGrantsPanelComponent, SelfActivationPanelComponent, TileImpersonationPanelComponent],
  template: `
    <!-- A plain page WITHIN the app chrome: the navbar, canvas and padded scroll area come from the shell, and
         this route deliberately keeps that chrome (chrome-free-routes.ts). So no own header / full-screen gate;
         just the centred content column. -->
    <div class="mx-auto max-w-4xl space-y-4">

        @if (accessExpired()) {
          <!-- #279 follow-up: ErrorInterceptor recovered the platform/base session silently after a time-boxed
               self-grant expired underneath it, rather than logging out. This is the one place that says so —
               shown ABOVE every other state (including the loading spinner) because the whole point is that the
               person is about to be asked to choose again, and a card that vanishes without explaining why the
               previous one stopped working reads as a bug rather than an expiry. -->
          <div class="rounded-2xl border-[1px] border-amber-100 bg-amber-50 p-4" role="status" aria-live="polite">
            <p class="text-sm font-semibold text-gray-900">Zugang abgelaufen</p>
            <p class="mt-1 text-sm text-gray-600">
              Ihr temporärer Zugang ist abgelaufen. Bitte wählen Sie erneut einen Bereich.
            </p>
          </div>
        }

        @if (loading()) {
          <div class="flex flex-col items-center gap-3 py-16" role="status" aria-live="polite">
            <div class="h-10 w-10 animate-spin rounded-full border-4 border-gray-200
                        border-t-theme-primary-600"></div>
            <p class="text-sm text-gray-500">Zugänge werden geladen…</p>
          </div>
        } @else if (error()) {
          <div class="rounded-2xl border-[1px] border-red-100 bg-red-50 p-4" role="alert">
            <p class="text-sm font-bold text-red-700">Zugänge konnten nicht geladen werden</p>
            <p class="mt-1 text-sm text-red-700">{{ error() }}</p>
            <button type="button" (click)="load()"
                    class="mt-3 inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 active:scale-95">
              Erneut versuchen
            </button>
          </div>
        } @else if (!choices().length && dormant().length) {
          <!-- #279, Defect A. This account DOES hold access — it just opens nothing yet — so the "Noch kein
               Zugang" card below would be a lie, and it is the lie that sends somebody off to ask for an
               invitation they have already redeemed. One card per company, naming it and saying what has to
               happen next. -->
          @for (entry of dormant(); track entry.companyId) {
            @let message = describeDormant(entry);
            <div class="rounded-2xl border-[1px] border-amber-100 bg-amber-50 p-4" role="status">
              <h1 class="text-xl font-bold tracking-tight text-gray-900">{{ message.title }}</h1>
              <!-- gray-600, not -500: this sits on a tinted surface, where -500 fails AA (style guide). -->
              <p class="mt-2 text-sm text-gray-600">{{ message.body }}</p>
            </div>
          }
          <div class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
            <p class="text-sm text-gray-500">
              Melden Sie sich ab, bis der Standort angelegt ist. Danach steht Ihnen die Firma hier offen.
            </p>
          </div>
        } @else if (!choices().length) {
          <div class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
            <h1 class="text-xl font-bold tracking-tight text-gray-900">Noch kein Zugang</h1>
            <p class="mt-2 text-sm text-gray-500">
              Diesem Konto wurde noch nirgends Zugriff erteilt. Lassen Sie sich einladen oder verknüpfen Sie Ihr
              bisheriges Login.
            </p>
          </div>
        } @else {
          <div class="rounded-2xl border-[1px] border-theme-primary-100/50 bg-theme-primary-50/50 p-4">
            <h1 class="text-xl font-bold tracking-tight text-gray-900">Wo möchten Sie arbeiten?</h1>
            <p class="mt-1 text-sm text-gray-600">
              Dieses Konto hat Zugriff auf mehrere Bereiche. Ihre Auswahl bestimmt, welche Anwendung sich öffnet
              und welche Rechte Ihre Sitzung erhält. Sie können später jederzeit unten links wechseln.
            </p>
          </div>

          @if (enterError()) {
            <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
              {{ enterError() }}
            </p>
          }

          <ul class="grid grid-cols-1 gap-3 md:grid-cols-2">
            @for (context of choices(); track key(context)) {
              <!-- flex-col, and the card is flex-1 rather than h-full: a grid item whose only child is
                   full-height sizes fine, but once the card has a SIBLING (the takeover action below it) that
                   percentage resolves against a height the sibling is also contributing to — the row keeps the
                   card's height and the action overflows on top of whatever follows the list.
                   (No backticks anywhere in this template: it is a template literal.) -->
              <li class="flex h-full flex-col">
                <button type="button" (click)="choose(context)" [disabled]="!selectable(context) || busy()"
                        class="w-full flex-1 rounded-2xl border-[1px] border-gray-100 bg-white p-4 text-left shadow-sm
                               transition-all duration-200 hover:shadow-md active:scale-[0.99]
                               disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:shadow-sm
                               focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                               focus-visible:outline-theme-primary-500">
                  <span class="flex items-baseline justify-between gap-2">
                    <span class="truncate text-base font-bold tracking-tight text-gray-800">
                      {{ title(context) }}
                    </span>
                    <span class="shrink-0 text-xs font-semibold text-theme-primary-700">{{ view(context) }}</span>
                  </span>
                  <span class="mt-0.5 block truncate text-sm text-gray-500">{{ scope(context) }}</span>

                  @if (context.roles.length) {
                    <span class="mt-3 flex flex-wrap gap-1">
                      @for (role of context.roles; track role) {
                        <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                                     font-semibold text-gray-600">{{ label(role) }}</span>
                      }
                    </span>
                  }

                  @if (!selectable(context)) {
                    <!-- Still true at THIS moment, and for a different reason than before: a platform principal
                         arrives here holding the platform session already, so entering it would mint a second,
                         equivalent one. The chrome switcher offers it as a destination because by then the
                         session has been narrowed into a tenant and genuinely is elsewhere. -->
                    <span class="mt-3 block text-xs text-gray-500">
                      Sie sind bereits hier — Sie können später jederzeit unten links zurückwechseln.
                    </span>
                  }
                </button>

                @if (canImpersonate(context)) {
                  <!-- OUTSIDE the card button, never inside it: a button nested in a button is invalid HTML and
                       browsers recover by dropping one of the two, with no rule about which. -->
                  <button type="button" (click)="openImpersonation(context)"
                          [attr.aria-expanded]="isImpersonationTarget(context)"
                          class="mt-1 w-full rounded-xl px-4 py-2 text-left text-xs font-semibold
                                 text-theme-primary-700 transition-all duration-200 hover:bg-theme-primary-50
                                 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                                 focus-visible:outline-theme-primary-500">
                    Konto übernehmen …
                  </button>
                } @else {
                  <!-- Reserve the takeover row's height so a tile without it does not stretch its card taller
                       than the tile that has it — that mismatch is what read as "tiles misaligned". -->
                  <span class="mt-1 block px-4 py-2 text-xs" aria-hidden="true">&nbsp;</span>
                }
              </li>
            }
          </ul>

          @if (impersonating(); as tile) {
            <auth-tile-impersonation-panel [context]="tile" (close)="impersonating.set(null)"/>
          }

          @if (isPlatformOwner()) {
            <!-- What already exists, before the form that creates more of it — a platform owner reaching this
                 panel is as likely to be checking on an earlier self-grant as issuing a new one. -->
            <auth-my-grants-panel #myGrants/>

            <!-- Below the tiles, because it is what you reach for when NONE of them is the tenant you need —
                 the picker's own answer for a platform owner, whose list is never their real list. load()
                 refreshes the tiles; myGrants.reload() refreshes the list above, which the new grant belongs
                 to (without it, the just-granted access does not appear until the page is reopened);
                 grantChanges.notifyChanged() tells any OTHER live view — the bottom switcher's tiles, chiefly,
                 since it is mounted on this same route — to refresh too (GrantChangeNotifierService). -->
            <auth-self-activation-panel (granted)="load(); myGrants.reload(); grantChanges.notifyChanged()"/>
          }

          @if (requiresSearch()) {
            <div class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
              <p class="text-sm font-bold text-gray-800">Nicht alle Firmen sind hier aufgeführt</p>
              <p class="mt-1 text-sm text-gray-500">
                Als Plattformverwaltung dürfen Sie grundsätzlich in jeder Firma arbeiten. Diese Liste zeigt nur
                Ihre eigenen Zuweisungen. Für eine weitere Firma schalten Sie sich oben selbst frei; danach
                erscheint sie hier als Kachel.
              </p>
            </div>
          }
        }
    </div>
  `
})
export class ChooseContextComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  /** Protected, not private: the template's self-activation host calls {@link GrantChangeNotifierService.notifyChanged} directly. */
  protected readonly grantChanges = inject(GrantChangeNotifierService);

  protected readonly loading = signal(true);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly enterError = signal<string | null>(null);
  private readonly options = signal<ContextOptions | null>(null);

  /** Whether `?notice=access-expired` was present on arrival — see the banner in the template. */
  protected readonly accessExpired = signal(false);

  protected readonly choices = computed(() => this.options()?.contexts ?? []);
  protected readonly requiresSearch = computed(() => this.options()?.requiresSearch ?? false);

  /**
   * Companies this account holds live grants in that open nothing (#279, Defect A).
   *
   * `?? []` rather than a required field: an older backend omits the key entirely, and this screen is the one a
   * session with nowhere to go lands on — it must not throw there of all places.
   */
  protected readonly dormant = computed<DormantContext[]>(() => this.options()?.dormant ?? []);

  /** The German heading + instruction for one dormant company. Shared with nothing else; see the models file. */
  protected describeDormant = dormantContextMessage;

  /**
   * The tile whose account list is open, or null.
   *
   * A signal on the chooser rather than state inside the panel, because only one may be open at a time and the
   * panel is mounted below the grid rather than inside a card — a card is a fixed-height tile in a two-column
   * grid, and an account list unfolding inside one would push its neighbour around.
   */
  protected readonly impersonating = signal<SelectableContext | null>(null);

  /**
   * Whether THIS session may use the two platform affordances.
   *
   * Read from the live session, not from `requiresSearch`: the two agree here (both mean "a platform
   * principal") but they answer different questions, and the endpoints these affordances call are gated on the
   * role. Gating the UI on the same fact the server gates on is what keeps a rendered button from being a
   * button that 403s.
   *
   * A plain method rather than a `computed`, deliberately: the session is not a signal, so a `computed` over it
   * would cache its first answer and never recompute — correct here only by accident, since entering a context
   * reloads the document anyway. A method cannot go stale.
   */
  protected isPlatformOwner(): boolean {
    return this.auth.isPlatformOwner;
  }

  /**
   * Whether a tile can be a takeover target: a real tenant, and a session that may impersonate.
   *
   * The platform shell is excluded because it is not a tenant — there is no company whose accounts could be
   * listed. An employee tile is excluded because employees are not impersonation targets and never have been:
   * the server's target lookup reads the main-DB `User` table, so offering it would render a button whose every
   * press ends in `E_IMPERSONATION_TARGET_NOT_FOUND`.
   */
  protected canImpersonate(context: SelectableContext): boolean {
    if (!this.isPlatformOwner()) return false;
    return context.kind === SelectableContextKind.Company || context.kind === SelectableContextKind.Shard;
  }

  /** Whether this tile's account list is the one currently open — drives `aria-expanded` on its button. */
  protected isImpersonationTarget(context: SelectableContext): boolean {
    const open = this.impersonating();
    return !!open && contextKey(open) === contextKey(context);
  }

  /** Opens the account list for a tile, or closes it when it is already the open one. */
  protected openImpersonation(context: SelectableContext): void {
    this.impersonating.set(this.isImpersonationTarget(context) ? null : context);
  }

  ngOnInit(): void {
    this.readAccessExpiredNotice();

    // Nothing to choose without an external identity: the backend reads the subject from the token's ext_id
    // claim and refuses a password session. Landing beats showing that refusal as an error the user cannot act on.
    if (!this.auth.canSelectContext) {
      this.land();
      return;
    }
    this.load();

    // Refreshes the tiles after a grant is created or revoked ANYWHERE in the app — chiefly a self-grant
    // issued from the bottom switcher's panel, which is mounted on this same route (see the class doc comment
    // on why the chrome is kept here). `load()` never calls `notifyChanged()` itself, so this cannot cascade.
    this.grantChanges.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
  }

  /**
   * Reads `?notice=access-expired` (set by `ErrorInterceptor`'s silent recovery, #279 follow-up) and strips it
   * from the URL once read — the same one-shot treatment `SignInNoticeComponent` gives `?notice=` on the
   * sign-in landing, so a reload or a shared link does not re-announce an expiry that already happened.
   *
   * Stripped whether or not the code was RECOGNISED, matching `SignInNoticeComponent`: a newer backend/build
   * pairing can send a code this one has no wording for yet, and leaving it behind would make it reappear on
   * every reload. `?pick` is left untouched — it is a SEPARATE signal (force the tiles, see `load()`) that this
   * notice rides alongside but does not own.
   */
  private readAccessExpiredNotice(): void {
    const code = new URLSearchParams(window.location.search).get('notice');
    if (!code) return;

    if (code === ContextRecoveryNotice.AccessExpired) this.accessExpired.set(true);

    const url = new URL(window.location.href);
    url.searchParams.delete('notice');
    history.replaceState({}, '', url.toString());
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.auth.availableContexts().subscribe({
      next: options => {
        this.options.set(options);

        // Exactly one option is not a choice. The backend says so with autoSelect, and honouring it is the
        // whole reason it exists: the common case in this product is one person at one shard, and making the
        // majority dismiss a one-option screen would be a daily irritation for no safety gain.
        //
        // `loading` deliberately STAYS set on both auto paths. Landing is a full document load, which does not
        // happen within this tick — clearing it first would paint "Wo möchten Sie arbeiten?" for a frame at
        // somebody who was never being asked, which is precisely the interruption autoSelect exists to avoid.
        //
        // `selectable`, not `canEnter`: the backend WOULD accept the platform shell now, but a platform
        // principal reaching this screen already holds that session, so landing is both correct and one
        // round trip cheaper than entering it again (see `needsEntryAfterSignIn`).
        // An explicit "?pick" (the switcher's "Zur Bereichsauswahl" button) means the operator ASKED for the
        // chooser, so the one-option auto-select and the platform-owner auto-land are both suppressed. Without it a
        // platform owner — whose autoSelect is the shell they already hold — is bounced straight back and can never
        // reach this screen again, which is exactly what "get me back to the switcher" has to defeat.
        const forcePicker = new URLSearchParams(window.location.search).has('pick');
        const auto = options?.autoSelect;
        if (!forcePicker && auto && this.selectable(auto)) { this.choose(auto); return; }
        if (!forcePicker && auto) { this.land(); return; }   // the platform shell — already the session we are in

        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        // The listing has no structured refusal worth branching on — it either answered or it did not — and the
        // server's English `description` must never be rendered (style guide).
        this.error.set('Ihre Bereiche konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      }
    });
  }

  protected choose(context: SelectableContext): void {
    if (!this.selectable(context) || this.busy()) return;
    this.busy.set(true);
    this.enterError.set(null);

    this.auth.enterContext(context).subscribe({
      next: () => this.land(),
      error: err => {
        const code = readErrorCode(err);

        // The company demands a second factor this account has not enrolled (#282/#279). Self-remediable, and
        // the remediation is a whole screen — so this is a navigation, not a message. Sending them anywhere else
        // would leave them re-pressing a card that cannot open until they have enrolled.
        //
        // Note the session is NOT restricted: it is an ordinary contextless one that may already reach the
        // enrolment endpoints. What was refused is this entry, and it will be granted on the next attempt. The
        // URL says so, because the guard cannot otherwise tell this apart from somebody standing on the
        // remediation screen with nothing to remediate — and used to bounce them straight back off it.
        if (isSelfRemediableContextRefusal(code)) {
          window.open(TWO_FACTOR_REMEDIATION_URL, '_self');
          return;
        }

        this.busy.set(false);
        // Also clears `loading`, so a FAILED auto-select does not strand the user on a spinner forever: it
        // falls back to the list it was about to skip, with the refusal shown above it.
        this.loading.set(false);
        // German, mapped from the CODE, by the mapping the switcher shares — see `context-entry-refusal.ts`.
        // The server's `description` is English by design and is a developer-facing string; rendering it was a
        // long-standing violation of the style guide's rule, and it is what somebody entering a two-factor
        // company would otherwise have been shown.
        this.enterError.set(contextEntryMessageFor(code));
      }
    });
  }


  /**
   * A full document load, not a router navigation: the shell is chosen from the session's roles at bootstrap,
   * and entering a context can change which shell is correct.
   */
  private land(): void {
    window.open('/', '_self');
  }

  /**
   * The platform shell is listed for orientation but is **not offered here**, even though the backend would
   * accept it (`canEnter` is true for it) and the chrome switcher does offer it.
   *
   * The two surfaces answer the same question at opposite moments, and the honest answer differs:
   *
   * - **Here**, immediately after sign-in, a platform principal is still holding the platform session — the
   *   token minted at sign-in carries its platform roles and no company. Entering would mint an equivalent
   *   second session, rotate the CM-Secret and reload the page to arrive where it already is. "Sie sind
   *   bereits hier" is a fact, not a limitation.
   * - **In the switcher**, the session has since entered a tenant, which narrowed `PlatformOwner` out of it.
   *   The platform shell is then genuinely elsewhere, and offering it is the only way back.
   *
   * Making it clickable in both places would trade a truthful message for a redundant round trip.
   */
  protected selectable(context: SelectableContext): boolean {
    return needsEntryAfterSignIn(context);
  }

  protected key = contextKey;
  protected title = contextTitle;
  protected scope = contextScope;
  protected label = roleLabel;

  protected view(context: SelectableContext): string {
    return viewLabel(context.view);
  }
}
